import React, { useState, useEffect } from 'react';
import { Users, Gift, TrendingUp, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import './ReferralSystem.css';

const ReferralSystem = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchReferralStats();
    }, []);

    const fetchReferralStats = async () => {
        try {
            setLoading(true);
            // 🔧 FIX: Check both localStorage AND sessionStorage for token
            // Email/password login without "Remember Me" stores token in sessionStorage
            // Google OAuth or "Remember Me" stores token in localStorage
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            
            if (!token) {
                setError('Please log in to view referral stats');
                setLoading(false);
                return;
            }
            
            const response = await fetch('/api/referrals/stats', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setError('Your session has expired. Please log in again.');
                    localStorage.removeItem('token');
                    setTimeout(() => window.location.href = '/login', 2000);
                    return;
                }
                if (response.status === 403) {
                    setError('You do not have permission to view this page.');
                    return;
                }
                if (response.status === 500) {
                    setError('Server error. Please try again later.');
                    return;
                }
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success) {
                setStats(data.stats);
                setError(null);
            } else {
                setError(data.message || 'Failed to load referral data');
            }
        } catch (err) {
            console.error('Error fetching referral stats:', err);
            
            if (err.name === 'TypeError' && err.message.includes('fetch')) {
                setError('Network error. Please check your connection.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const copyReferralLink = () => {
        const referralUrl = `${window.location.origin}/signup?ref=${stats.referral_code}`;
        navigator.clipboard.writeText(referralUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading) {
        return (
            <div className="referral-loading">
                <div className="spinner"></div>
                <p>Loading referral data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="referral-error">
                <div className="error-icon">⚠️</div>
                <h3>Unable to Load Referral Data</h3>
                <p className="error-message">{error}</p>
                <div className="error-actions">
                    <button onClick={fetchReferralStats} className="retry-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                        </svg>
                        Retry
                    </button>
                    <button onClick={() => window.location.reload()} className="refresh-btn">
                        Refresh Page
                    </button>
                </div>
            </div>
        );
    }

    if (!stats) {
        return null;
    }

    return (
        <div className="referral-system">
            {/* Stats Overview */}
            <div className="referral-stats-grid">
                <div className="stat-card">
                    <Users className="stat-icon" />
                    <div className="stat-content">
                        <p className="stat-label">Total Referrals</p>
                        <p className="stat-value">{stats.total_referrals}</p>
                    </div>
                </div>

                <div className="stat-card validated">
                    <CheckCircle className="stat-icon" />
                    <div className="stat-content">
                        <p className="stat-label">Validated</p>
                        <p className="stat-value">{stats.validated_referrals}</p>
                    </div>
                </div>

                <div className="stat-card pending">
                    <TrendingUp className="stat-icon" />
                    <div className="stat-content">
                        <p className="stat-label">Pending</p>
                        <p className="stat-value">{stats.pending_referrals}</p>
                    </div>
                </div>

                <div className="stat-card rewards">
                    <Gift className="stat-icon" />
                    <div className="stat-content">
                        <p className="stat-label">Total Rewards</p>
                        <p className="stat-value">{stats.total_rewards?.toLocaleString() || 0} TPHILE</p>
                    </div>
                </div>
            </div>

            {/* Referral Code Section */}
            <div className="referral-code-section">
                <h3>Your Referral Link</h3>
                <div className="referral-code-box">
                    <div className="code-display">
                        <p className="code-label">Referral Code:</p>
                        <p className="code-value">{stats.referral_code}</p>
                    </div>
                    <div className="link-input-group">
                        <input
                            type="text"
                            readOnly
                            value={`${window.location.origin}/signup?ref=${stats.referral_code}`}
                            className="referral-link-input"
                        />
                        <button 
                            onClick={copyReferralLink}
                            className={`copy-btn ${copied ? 'copied' : ''}`}
                        >
                            {copied ? <CheckCircle size={20} /> : <Copy size={20} />}
                            {copied ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Referral List */}
            <div className="referrals-list-section">
                <h3>Your Referrals</h3>
                {stats.total_referrals === 0 ? (
                    <div className="empty-state">
                        <Users size={48} />
                        <p>No referrals yet</p>
                        <p className="empty-subtitle">Share your referral link to get started!</p>
                    </div>
                ) : (
                    <div className="referrals-list">
                        {stats.referrals.map((referral) => (
                            <div key={referral.id} className={`referral-item ${referral.status}`}>
                                <div className="referral-info">
                                    <div className="referral-username">
                                        <Users size={16} />
                                        {referral.referee_username}
                                    </div>
                                    <div className="referral-meta">
                                        <span className="referral-date">
                                            Joined: {new Date(referral.referred_at).toLocaleDateString()}
                                        </span>
                                        {referral.status === 'validated' && referral.validated_at && (
                                            <span className="validation-date">
                                                Validated: {new Date(referral.validated_at).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="referral-status-section">
                                    <span className={`status-badge ${referral.status}`}>
                                        {referral.status === 'validated' ? '✓ Validated' : '⏳ Pending'}
                                    </span>
                                    <div className="predictions-count">
                                        {referral.qualifying_predictions}/3 predictions
                                    </div>
                                    {referral.status === 'validated' && (
                                        <div className="reward-earned">
                                            +{referral.reward_amount?.toLocaleString()} TPHILE
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* How It Works */}
            <div className="referral-info-box">
                <h4>How Referrals Work</h4>
                <ul>
                    <li>Share your unique referral link with friends</li>
                    <li>They sign up using your link</li>
                    <li>When they create 3 quality predictions (70+ score), the referral is validated</li>
                    <li>You earn 10,000 TPHILE tokens per validated referral!</li>
                </ul>
            </div>
        </div>
    );
};

export default ReferralSystem;
